import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';

import './index.css';

import { bootstrap, currentHost, i18n, queryClient } from '@saas-kit/lib';
import { Toaster } from '@saas-kit/ui';

import { AppShell } from './AppShell';
import { AuthApp } from './AuthApp';
import { ApiKeysPage } from './features/api-keys';
import { BillingPage } from './features/billing';
import { FilesPage } from './features/files';
import { HomePage } from './features/home';
import { BackendInvitationsPage } from './features/invitations';
import { MembersPage } from './features/members';
import { NotificationsPage } from './features/notifications';
import { ProfilePage } from './features/profile';
import {
  DisplayEditPage,
  DisplaysListPage,
  KioskPage,
  QmsDisplayBoardPage,
  QmsIssuePage,
  QmsSetupPage,
  QmsWorkstationPage,
} from './features/qms';
import { RolesPage } from './features/rbac';
import { RequireAuth } from './RequireAuth';
import { RequireTenant } from './RequireTenant';
import { WrongHost } from './WrongHost';

const expectedHost = (import.meta.env.VITE_APP_HOST ?? 'app.kit.local').toLowerCase();
const onCorrectHost = currentHost() === expectedHost;

if (onCorrectHost) {
  void bootstrap();
}

const root = createRoot(document.getElementById('root')!);
root.render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Toaster />
          {onCorrectHost ? (
            <Routes>
              <Route path="/auth/*" element={<AuthApp />} />
              <Route
                path="/qms/display/:slug"
                element={<QmsDisplayBoardPage />}
              />
              <Route path="/qms/kiosk" element={<KioskPage />} />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <RequireTenant>
                      <AppShell />
                    </RequireTenant>
                  </RequireAuth>
                }
              >
                <Route index element={<HomePage />} />
                <Route path="team" element={<MembersPage />} />
                <Route path="team/invitations" element={<BackendInvitationsPage />} />
                <Route path="team/roles" element={<RolesPage />} />
                <Route path="files" element={<FilesPage />} />
                <Route path="api-keys" element={<ApiKeysPage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="qms/setup" element={<QmsSetupPage />} />
                <Route path="qms/workstation" element={<QmsWorkstationPage />} />
                <Route path="qms/issue" element={<QmsIssuePage />} />
                <Route
                  path="qms/displays"
                  element={<DisplaysListPage />}
                />
                <Route
                  path="qms/displays/new"
                  element={<DisplayEditPage />}
                />
                <Route
                  path="qms/displays/:id"
                  element={<DisplayEditPage />}
                />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          ) : (
            <WrongHost expected={expectedHost} />
          )}
        </BrowserRouter>
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
